/**
 * BootSequence Component - HAL 3000
 * Version 4.0.0 - Diagnostic temps réel des services au démarrage
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Terminal, SkipForward, Bug, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const api = axios.create({ baseURL: API_BASE });

// Audio Context pour sons 8-bit
let audioCtx = null;
const getAudioContext = () => {
  if (!audioCtx) {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      return null;
    }
  }
  return audioCtx;
};

// Son 8-bit bip
const playBeep = (freq = 800, duration = 0.05, type = 'square') => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = type;
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  } catch (e) {}
};

const playOkSound = () => playBeep(1200, 0.04, 'square');
const playAlertSound = () => playBeep(300, 0.1, 'square');
const playLineSound = () => playBeep(600, 0.02, 'square');
const playBootComplete = () => {
  const notes = [523, 659, 784, 1047];
  notes.forEach((freq, i) => {
    setTimeout(() => playBeep(freq, 0.12, 'square'), i * 100);
  });
};

const BootSequence = ({ onComplete }) => {
  const [bootLines, setBootLines] = useState([]);
  const [progress, setProgress] = useState(0);
  const [currentLine, setCurrentLine] = useState(0);
  const [bootComplete, setBootComplete] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [diagnosticPhase, setDiagnosticPhase] = useState(false);
  const terminalRef = useRef(null);

  // Lignes de boot initiales (avant diagnostic)
  const bootSequenceLines = [
    { text: "HAL 3000 - SYSTÈME DOMOTIQUE v7.0", color: "#00f0ff", icon: "system" },
    { text: "https://hal3000.fr", color: "#888" },
    { text: "" },
    { text: "[BIOS] Vérification intégrité mémoire... [OK]", color: "#00ff9f", icon: "ok" },
    { text: "[BOOT] Initialisation noyau système... [OK]", color: "#00ff9f", icon: "ok" },
    { text: "[NET] Établissement liaison sécurisée... [OK]", color: "#00ff9f", icon: "ok" },
    { text: "[DATABASE] Connexion MongoDB établie... [OK]", color: "#00ff9f", icon: "ok" },
    { text: "" },
  ];

  // Skip function
  const skipBoot = useCallback(() => {
    playBootComplete();
    setTimeout(() => onComplete?.(), 400);
  }, [onComplete]);

  // Lancer le diagnostic des services
  const runDiagnostic = useCallback(async () => {
    setDiagnosticPhase(true);
    setBootLines(prev => [...prev, 
      { text: "" },
      { text: "━━━━━━━━━━━ DIAGNOSTIC SERVICES ━━━━━━━━━━━", color: "#ff9900" },
    ]);
    
    try {
      const response = await api.get('/system/full-diagnostic');
      const data = response.data;
      setDiagnosticResults(data);
      
      const services = data.services || {};
      const serviceNames = {
        tuya: "SmartLife/Tuya",
        vicare: "Chaudière Viessmann",
        homeconnect: "Home Connect",
        pellet_stove: "Poêle à Pellets",
        enedis: "Enedis Électricité",
        growatt: "Panneaux Solaires",
        roomba: "iRobot Roomba",
        mg4: "Véhicule MG4",
        voicemonkey: "Voice Monkey"
      };
      
      for (const [key, status] of Object.entries(services)) {
        await new Promise(r => setTimeout(r, 100));
        const name = serviceNames[key] || key;
        const st = status.status;
        
        let line;
        if (st === 'healthy') {
          line = { text: `[${name}] Connecté... [OK]`, color: "#00ff9f", icon: "ok" };
          playOkSound();
        } else if (st === 'warning') {
          const msg = status.message || status.error || 'Attention';
          line = { text: `[${name}] ${msg.substring(0, 40)}... [WARN]`, color: "#ff9900", icon: "alert" };
          playLineSound();
        } else {
          const msg = status.message || status.error || 'Erreur';
          line = { text: `[${name}] ${msg.substring(0, 40)}... [ERREUR]`, color: "#ff4444", icon: "alert" };
          playAlertSound();
        }
        setBootLines(prev => [...prev, line]);
      }
      
      // Résumé
      const summary = data.summary || {};
      await new Promise(r => setTimeout(r, 200));
      setBootLines(prev => [...prev,
        { text: "" },
        { text: `RÉSUMÉ: ${summary.healthy || 0} OK | ${summary.warning || 0} WARN | ${summary.critical || 0} ERREUR`, 
          color: summary.critical > 0 ? "#ff4444" : (summary.warning > 0 ? "#ff9900" : "#00ff9f") }
      ]);
      
    } catch (err) {
      setBootLines(prev => [...prev, 
        { text: `[DIAGNOSTIC] Erreur: ${err.message}`, color: "#ff4444", icon: "alert" }
      ]);
    }
    
    // Fin du boot
    await new Promise(r => setTimeout(r, 500));
    setBootLines(prev => [...prev,
      { text: "" },
      { text: "CENTRE DE COMMANDE PRÊT", color: "#00f0ff", large: true }
    ]);
    setBootComplete(true);
    playBootComplete();
    setTimeout(() => onComplete?.(), 1500);
  }, [onComplete]);

  // Boot sequence avec sons
  useEffect(() => {
    if (currentLine < bootSequenceLines.length) {
      const timeout = setTimeout(() => {
        const line = bootSequenceLines[currentLine];
        setBootLines(prev => [...prev, line]);
        setCurrentLine(currentLine + 1);
        setProgress(((currentLine + 1) / (bootSequenceLines.length + 10)) * 100);
        
        if (line.icon === 'ok') playOkSound();
        else if (line.icon === 'alert') playAlertSound();
        else if (line.text && line.text.length > 0) playLineSound();
      }, 80);
      return () => clearTimeout(timeout);
    } else if (!diagnosticPhase) {
      // Lancer le diagnostic après les lignes de base
      runDiagnostic();
    }
  }, [currentLine, diagnosticPhase, runDiagnostic]);

  // Auto-scroll
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [bootLines]);

  const getProgressBar = () => {
    const filled = Math.floor(progress / 5);
    const empty = 20 - filled;
    return '▓'.repeat(filled) + '░'.repeat(empty);
  };

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden">
      {/* Scanlines effect */}
      <div className="absolute inset-0 pointer-events-none opacity-20" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,240,255,0.03) 2px, rgba(0,240,255,0.03) 4px)'
      }} />
      
      {/* Grid background */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle, #00f0ff 1px, transparent 1px)',
        backgroundSize: '30px 30px'
      }} />

      <div className="w-full max-w-4xl mx-auto p-4 relative z-10">
        {/* Terminal Window */}
        <div className="bg-black border-2 border-cyan-400 rounded-lg" style={{
          boxShadow: '0 0 30px rgba(0, 240, 255, 0.4)'
        }}>
          {/* Terminal Header */}
          <div className="bg-cyan-900/30 border-b-2 border-cyan-400 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Terminal className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 font-mono font-bold text-sm uppercase tracking-wider">
                BOOT_DIAGNOSTIC_TERMINAL
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* DEBUG Button */}
              <button
                onClick={() => setDebugMode(!debugMode)}
                className={`px-2 py-1 border rounded text-xs font-mono flex items-center gap-1 transition-all ${
                  debugMode 
                    ? 'bg-purple-600 border-purple-400 text-white' 
                    : 'bg-purple-900/50 hover:bg-purple-700/50 border-purple-400 text-purple-400'
                }`}
              >
                <Bug className="w-3 h-3" />
                DEBUG
              </button>
              {/* SKIP Button */}
              <button
                onClick={skipBoot}
                className="px-3 py-1 bg-cyan-900/50 hover:bg-cyan-700/50 border border-cyan-400 rounded text-cyan-400 font-mono text-xs uppercase flex items-center gap-1 transition-all"
              >
                <SkipForward className="w-3 h-3" />
                SKIP
              </button>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-green-400 text-xs font-mono">ONLINE</span>
              </div>
            </div>
          </div>

          {/* Terminal Content */}
          <div 
            ref={terminalRef} 
            className="p-4 font-mono text-sm h-80 overflow-y-auto"
            style={{ scrollBehavior: 'smooth' }}
          >
            {bootLines.map((line, index) => (
              <div
                key={index}
                className={`mb-0.5 ${line.large ? 'text-lg font-bold mt-4 mb-2' : ''}`}
                style={{ color: line.color || '#00f0ff' }}
              >
                {line.icon === 'ok' && <span className="mr-2 text-green-400">✓</span>}
                {line.icon === 'alert' && <span className="mr-2 text-yellow-400">⚠</span>}
                {line.icon === 'system' && <span className="mr-2 text-cyan-400">▶</span>}
                {line.text}
              </div>
            ))}
            
            {/* Debug Info */}
            {debugMode && diagnosticResults && (
              <div className="mt-4 p-2 border border-purple-500/50 rounded bg-purple-900/20 text-xs">
                <div className="text-purple-400 font-bold mb-2">═══ MODE DEBUG ═══</div>
                <pre className="text-purple-300 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(diagnosticResults, null, 2)}
                </pre>
              </div>
            )}
            
            {/* Cursor */}
            {!bootComplete && (
              <span className="inline-block w-2 h-4 bg-cyan-400 animate-pulse ml-1" />
            )}
          </div>

          {/* Progress Bar */}
          {!bootComplete && (
            <div className="px-4 pb-3 border-t border-cyan-400/30 pt-2">
              <div className="text-cyan-400 text-xs mb-1 font-mono">
                {diagnosticPhase ? 'DIAGNOSTIC SERVICES...' : 'INITIALISATION...'}
              </div>
              <div className="text-cyan-400 font-mono tracking-wider">
                [{getProgressBar()}]
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-3 text-center">
          <span className="text-gray-600 text-xs font-mono">
            HAL3000.FR | SYSTÈME SÉCURISÉ | v4.0.0-DIAG
          </span>
        </div>
      </div>
    </div>
  );
};

export default BootSequence;
